import { Globe } from 'lucide-react';
import type { EventType } from '../api/eventTypes';
import type { FieldErrors } from '../api/events';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../ui/Field';

/**
 * The fields an event is made of, shared by the create and edit screens.
 *
 * One definition because the two forms have to stay identical: an edit that offered a subset of
 * the boxes would make some facts correctable and others not, which is the failure the whole
 * ticket exists to fix. What differs between them belongs to the screens — the thread audience is
 * chosen once, when the thread is created, and never again.
 *
 * Times are collected as **local readings** and sent as such — `2026-10-30T17:00`, no zone. A
 * `datetime-local` input produces exactly that, and neither screen converts it: the unit's zone is
 * what "5pm Friday" means, and the browser is regularly in a different one. The zone in force is
 * stated above the fields rather than left to be inferred, because a campout that crosses a
 * daylight-saving change resolves each end differently and the leader should be able to see which
 * zone made that decision.
 */
export interface EventFormValues {
  /** The type's id, or '' for none. Never required — see the picker below. */
  eventTypeId: string;
  title: string;
  description: string;
  locationName: string;
  locationAddress: string;
  startsAt: string;
  endsAt: string;
  departsAt: string;
  returnsAt: string;
}

/**
 * Which of the four coordination questions this event asks — TROOP-118.
 *
 * **Deliberately not part of {@link EventFormValues}.** That interface is all-strings, and the
 * prefill machinery leans on it: `prefillFrom` treats a falsy value as "nothing to offer" and
 * `NewEventScreen` clears an un-prefilled box by assigning `''`. Both are exactly wrong for a
 * boolean — `false` is an answer, not an empty box — so the flags travel separately rather than
 * quietly breaking two loops that read `keyof EventFormValues`.
 */
export interface EventRequires {
  rsvp: boolean;
  forms: boolean;
  packing: boolean;
  travel: boolean;
  program: boolean;
}

/** All five on, which is what an event asks for unless a kind or a leader says otherwise. */
export const allRequired: EventRequires = {
  rsvp: true,
  forms: true,
  packing: true,
  travel: true,
  program: true,
};

export const emptyEventForm: EventFormValues = {
  eventTypeId: '',
  title: '',
  description: '',
  locationName: '',
  locationAddress: '',
  startsAt: '',
  endsAt: '',
  departsAt: '',
  returnsAt: '',
};

/**
 * Roots in their own order, each with the types that refine it underneath.
 *
 * A child whose parent is missing from the list — archived, most likely — is promoted to a root
 * rather than dropped. Losing it would take a live type out of the picker because of a decision
 * made about a different type.
 */
function groupByParent(types: EventType[]): { root: EventType; children: EventType[] }[] {
  const ids = new Set(types.map((t) => t.id));
  const roots = types.filter((t) => !t.parentEventTypeId || !ids.has(t.parentEventTypeId));

  return roots.map((root) => ({
    root,
    children: types.filter((t) => t.parentEventTypeId === root.id),
  }));
}

/**
 * The resolution of every time on this form, in seconds, which is the unit `step` takes.
 *
 * Nothing a unit schedules happens at 7:07. Quarter hours turn the minute field into a four-item
 * choice instead of a sixty-item one, which is the difference between picking a time and typing
 * one on a phone. The API and the domain still accept any minute: this is the form being
 * opinionated, not a rule about what an event may be.
 */
const QUARTER_HOUR = 15 * 60;

/** Said under every box a kind filled. Short, because it appears several times at once. */
const FROM_THE_KIND = 'Filled in from the kind you picked. Change it if this one is different.';

/** An empty box is an absent fact, not an empty string — the same rule the service applies. */
export function eventFormPayload(values: EventFormValues, requires?: EventRequires) {
  return {
    // Absent means "the leader did not say", which the server answers from the kind and then from
    // its own default of on. The forms always send all four, because the leader has seen them.
    requiresRsvp: requires?.rsvp,
    requiresForms: requires?.forms,
    requiresPacking: requires?.packing,
    requiresTravel: requires?.travel,
    requiresProgram: requires?.program,
    // '' is the "no type" option, which is a real answer rather than an unfilled box.
    eventTypeId: values.eventTypeId || null,
    title: values.title.trim(),
    description: values.description.trim() || null,
    locationName: values.locationName.trim() || null,
    locationAddress: values.locationAddress.trim() || null,
    startsAt: values.startsAt,
    endsAt: values.endsAt,
    departsAt: values.departsAt || null,
    returnsAt: values.returnsAt || null,
  };
}

/** The reason the submit button is unavailable, or undefined when it is not. */
export function incompleteReason(values: EventFormValues): string | undefined {
  if (!values.title.trim()) return 'Give the event a name.';
  if (!values.startsAt || !values.endsAt) return 'Set when it starts and ends.';
  return undefined;
}

/**
 * The five toggles, in the order the docket renders their rows.
 *
 * The copy names what the event *does*, not what the row is called: "Who's coming" rather than
 * "RSVP", "Things to bring" rather than "Packing" — the same reason the product says "what to
 * bring" everywhere, so the feature does not read as being about campouts.
 */
const REQUIREMENTS: {
  key: keyof EventRequires;
  label: string;
  hint: string;
}[] = [
  {
    key: 'rsvp',
    label: "Who's coming",
    hint: 'Ask everyone to say whether they will be there.',
  },
  {
    key: 'forms',
    label: 'Forms and permission slips',
    hint: 'Ask families to return something before the day.',
  },
  {
    key: 'packing',
    label: 'Things to bring',
    hint: 'Share a list, and let people tick off what they have.',
  },
  {
    key: 'travel',
    label: 'Getting there and back',
    hint: 'Work out who is driving, and how many seats that leaves.',
  },
  {
    key: 'program',
    label: 'What we are doing',
    // Names the act, not the office: any officer writes this, and the one who usually does
    // is a Scout. Saying "leaders" here would describe a rule the product does not have.
    hint: 'Plan the meeting -- a theme, and what happens when.',
  },
];

export function EventRequirementFields({
  requires,
  onChange,
}: {
  requires: EventRequires;
  onChange: (patch: Partial<EventRequires>) => void;
}) {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend className="t-label" style={{ color: 'var(--color-text-muted)', padding: 0 }}>
        What this event needs
      </legend>

      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 var(--space-3)' }}
      >
        Turn off anything this one does not use and its section disappears from the event page. A
        Monday meeting rarely needs any of them.
      </p>

      <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
        {REQUIREMENTS.map((r) => (
          <CheckboxField
            key={r.key}
            label={r.label}
            hint={r.hint}
            checked={requires[r.key]}
            onChange={(checked) => onChange({ [r.key]: checked })}
          />
        ))}
      </div>
    </fieldset>
  );
}

export function EventFormFields({
  values,
  onChange,
  fieldErrors,
  zoneNote,
  eventTypes,
  prefilled = {},
  onChooseType,
}: {
  values: EventFormValues;
  onChange: (patch: Partial<EventFormValues>) => void;
  fieldErrors: FieldErrors;
  zoneNote: string;
  /** The unit's live types. Empty until they load, and empty is a legitimate steady state too. */
  eventTypes: EventType[];
  /**
   * Which boxes the chosen kind filled in. Said out loud under each one, because a leader should
   * know what they are accepting rather than discover it after publishing.
   */
  prefilled?: Partial<Record<keyof EventFormValues, true>>;
  /**
   * Picking a kind, which on the create screen also fills the boxes it has answers for. Separate
   * from `onChange` because it is not a field edit: the two screens answer it differently, and an
   * edit form that prefilled would overwrite what a leader had already decided.
   */
  onChooseType: (eventTypeId: string) => void;
}) {
  const errorFor = (field: string) => fieldErrors[field]?.[0];

  // Only where there is no error to show: a field cannot be both "here is where this came from"
  // and "here is what is wrong with it", and the error is the more urgent of the two.
  const fromTypeHint = (field: keyof EventFormValues, hint?: string) =>
    prefilled[field] ? FROM_THE_KIND : hint;

  // A type the event already carries but the unit has since archived. It is not in the live list,
  // and dropping it from the picker would silently retype the event on the next save -- so it is
  // added back, named, and only for this event.
  const archivedButCarried =
    values.eventTypeId && !eventTypes.some((t) => t.id === values.eventTypeId);

  return (
    <>
      {/* First, because it is the question that frames the rest: what kind of thing is this.
          Optional on purpose -- the fastest path in the product is a leader posting a campout in
          fifteen seconds, and a required picker stands in front of it. */}
      {eventTypes.length > 0 && (
        <SelectField
          label="Kind"
          value={values.eventTypeId}
          error={errorFor('eventTypeId')}
          onChange={(e) => onChooseType(e.target.value)}
          hint="Your unit's own words for the things it runs."
        >
          <option value="">No particular kind</option>
          {groupByParent(eventTypes).map(({ root, children }) =>
            children.length === 0 ? (
              <option key={root.id} value={root.id}>
                {root.name}
              </option>
            ) : (
              // The shape is visible where a leader actually chooses. A native optgroup is not
              // selectable itself, so the root gets an entry inside its own group -- "Campout" has
              // to stay pickable for the campout that is no particular kind of campout.
              <optgroup key={root.id} label={root.name}>
                <option value={root.id}>{root.name}</option>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </optgroup>
            ),
          )}
          {archivedButCarried && (
            <option value={values.eventTypeId}>
              (this event&rsquo;s current kind, no longer offered)
            </option>
          )}
        </SelectField>
      )}

      <TextField
        label="Name"
        required
        value={values.title}
        error={errorFor('title')}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="Fall Camporee at Camp Wanocksett"
      />

      <TextAreaField
        label="Details"
        value={values.description}
        error={errorFor('description')}
        onChange={(e) => onChange({ description: e.target.value })}
        hint={fromTypeHint('description', "What families need to know that isn't a time or a place.")}
      />

      {/* The zone stated before the time fields, not after. Everything below is read in it,
          including each end of a trip that crosses a daylight-saving change. */}
      <p
        className="t-caption inline-flex items-center gap-2"
        style={{ color: 'var(--color-text-secondary)', margin: 0 }}
      >
        <Globe size={14} aria-hidden="true" />
        {zoneNote}
      </p>

      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))' }}>
        <TextField
          label="Starts"
          type="datetime-local"
          step={QUARTER_HOUR}
          required
          value={values.startsAt}
          error={errorFor('startsAt')}
          onChange={(e) => onChange({ startsAt: e.target.value })}
          hint={fromTypeHint('startsAt')}
        />
        <TextField
          label="Ends"
          type="datetime-local"
          step={QUARTER_HOUR}
          required
          value={values.endsAt}
          error={errorFor('endsAt')}
          onChange={(e) => onChange({ endsAt: e.target.value })}
          hint={fromTypeHint('endsAt')}
        />
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))' }}>
        <TextField
          label="Depart"
          type="datetime-local"
          step={QUARTER_HOUR}
          value={values.departsAt}
          error={errorFor('departsAt')}
          onChange={(e) => onChange({ departsAt: e.target.value })}
          hint="When the group actually leaves, if that differs from the start."
        />
        <TextField
          label="Return"
          type="datetime-local"
          step={QUARTER_HOUR}
          value={values.returnsAt}
          error={errorFor('returnsAt')}
          onChange={(e) => onChange({ returnsAt: e.target.value })}
        />
      </div>

      <TextField
        label="Place"
        value={values.locationName}
        error={errorFor('locationName')}
        onChange={(e) => onChange({ locationName: e.target.value })}
        hint={fromTypeHint('locationName')}
        placeholder="Camp Wanocksett"
      />

      <TextField
        label="Address"
        value={values.locationAddress}
        error={errorFor('locationAddress')}
        onChange={(e) => onChange({ locationAddress: e.target.value })}
        hint={fromTypeHint('locationAddress')}
        placeholder="50 Wanocksett Rd, Dublin, NH 03444"
      />
    </>
  );
}

/**
 * Field messages arrive camelCased from ASP.NET already, but a stray `StartsAt` would otherwise
 * render nowhere at all — a validation failure that looks like a form that did nothing.
 */
export function normaliseFieldErrors(errors: FieldErrors): FieldErrors {
  return Object.fromEntries(
    Object.entries(errors).map(([k, v]) => [k[0].toLowerCase() + k.slice(1), v]),
  );
}
